// ** React
import { useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import {
	getFollowing,
	removeFollowings,
} from "store/modules/userpanel-friends";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Components
import Text from "components/typography/Text";
import TableView from "components/layout/TableView";
import LoginAvatarSmall from "components/userpanel/LoginAvatarSmall";
import Pagination from "components/layout/Pagination";
import LoadingContainer from "components/layout/LoadingContainer";
import Skeleton from "components/layout/Skeleton";
import DatePresenter from "components/other/DatePresenter";
import ConvertLocation from "components/other/ConvertLocation";

// ** Hooks
import useProcess from "hooks/useProcess";
import useSelected from "hooks/useSelected";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const Following = ({
	getFollowing,
	removeFollowings,
	whileRemoving,
	loading,
	loaded,
	users,
	pagination,
	route,
	match: {
		params: { page },
	},
	suspension,
}) => {
	const {
		selected,
		hasSelected,
		isChecked,
		isCheckedAll,
		check,
		checkAll,
		checkAllDisabled,
		reset,
	} = useSelected(users.map((user) => user.id));

	const location = useLocation();

	const { isPhone } = useContext(deviceTypeContext);

	const userId = useSelector((state) => state.globalUser?.id);

	const currentPage = Number(page || 1);
	const { t } = useTranslation(["common", "userpanel", "suspensions"]);
	const {
		process,
		loadingContainerProps,
		loading: processLoading,
		ready: processReady,
	} = useProcess({
		promise: () => getFollowing(userId, currentPage),
	});

	const isBan = suspension?.duration > 0;
	const tooltipInfo = isBan ? t("suspensions:bannedAccount") : "";

	useEffect(() => {
		process();
	}, [location.key]);

	const handleRemove = () => {
		removeFollowings(selected).then(() => {
			reset();
			process();
		});
	};
	const columns = [
		{
			key: "check",
			headerName: "",
			headerComponent: () => {
				return (
					<TableView.CheckboxSelectAll
						checked={isCheckedAll}
						onChange={() => checkAll()}
						disabled={checkAllDisabled}
						withActiveBackgroundColor
					/>
				);
			},
			headerCellProps: {
				min: true,
			},
			rowComponent: ({ data }) => {
				return (
					<TableView.CheckboxSelectRow
						checked={isChecked(data.id)}
						onChange={() => check(data.id)}
						label={data.login}
						rowKey={data.rowKey}
						withActiveBackgroundColor
					/>
				);
			},
		},
		{
			key: "friend",
			isMain: true,
			headerName: t("common:person"),
			headerCellProps: {
				lowerCheckPadding: isPhone,
			},
			rowCellProps: {
				lowerCheckPadding: isPhone,
			},
			rowComponent: ({ data } = {}) => {
				return (
					<LoginAvatarSmall
						direction={false}
						data={data}
						maxWidthWithEllipsis={isPhone ? 200 : 110}
						additional={() => {
							const text = () => {
								if (data.sex === "couple") {
									if (data.partner_1.age === null) {
										return "";
									}
									return `${data.partner_1.age} ${t(
										"common:years"
									)}, ${data.partner_2.age} ${t(
										"common:years"
									)}, `;
								}

								if (data.user_data.age === null) {
									return "";
								}
								return `${data.user_data.age} ${t(
									"common:years"
								)}, `;
							};

							return (
								isPhone && (
									<>
										<Text bold>
											{t(`common:sexOptions.${data.sex}`)}
											{", "}
											{text()}
											<ConvertLocation
												city={data.city}
												country={data.country}
											/>
										</Text>
										<Text color="grey" small fullLine>
											{t(
												"userpanel:friends.headersName.dateAdded"
											)}
											: <DatePresenter date={data.date} />
										</Text>
									</>
								)
							);
						}}
					/>
				);
			},
		},
		{
			key: "gender",
			showInMain: isPhone,
			headerName: t("common:gender"),
			rowComponent: ({ data }) => {
				return (
					<Text textNowrap>{t(`common:sexOptions.${data.sex}`)}</Text>
				);
			},
		},
		{
			key: "age",
			showInMain: isPhone,
			headerName: t("userpanel:friends.headersName.age"),
			rowComponent: ({ data }) => {
				if (data.sex === "couple") {
					if (data.partner_1.age === null) {
						return "-";
					}
					return `${data.partner_1.age} ${t("common:years")}, ${
						data.partner_2.age
					} ${t("common:years")}`;
				}

				if (data.user_data.age === null) {
					return "-";
				}
				return `${data.user_data.age} ${t("common:years")}`;
			},
		},
		{
			key: "place",
			showInMain: isPhone,
			headerName: t("userpanel:friends.headersName.location"),
			rowComponent: ({ data }) => {
				return (
					<Text>
						<ConvertLocation
							city={data.city}
							country={data.country}
						/>
					</Text>
				);
			},
		},
		{
			key: "lastActive",
			showInMain: isPhone,
			headerName: t("userpanel:friends.headersName.dateAdded"),
			// dataKey: "date",
			rowComponent: ({ data }) => <DatePresenter date={data.date} />,
		},
	];

	return (
		<Skeleton
			main={
				<LoadingContainer
					{...loadingContainerProps}
					ready={processReady || users.length > 0}
					loading={processLoading || loading}
					noContentText={t("userpanel:friends.followingEmpty")}
					empty={loaded && users.length === 0}
					loaderDefault
				>
					<TableView
						data={users}
						columns={columns}
						actions={{
							items: [
								{
									onClick: handleRemove,
									icon: "user-minus",
									label: t("common:delete"),
									tooltipInfo: hasSelected ? tooltipInfo : "",
								},
							],
							disabled: !hasSelected || isBan || whileRemoving,
						}}
					/>
				</LoadingContainer>
			}
			pagination={
				pagination?.totalpages > 1 && (
					<Pagination
						total={pagination.totalpages}
						current={currentPage}
						path={route.path}
					/>
				)
			}
		/>
	);
};

// #####################################################

const mapStateToProps = ({
	userpanelFriends: { following },
	user: { suspension },
}) => ({
	suspension,
	...following,
});

export default connect(mapStateToProps, {
	getFollowing,
	removeFollowings,
})(Following);

// #####################################################
